import { Flex } from "@minervaproject/fds";
import { useEffect, useRef, useState } from "react";
import { Id, toast } from "react-toastify";

import { createCaptionerIsInAnotherRoomToast } from "components/shared/toasts";
import { useCapability } from "hooks/capabilities";
import { useAppSelector } from "hooks/redux_hooks";
import { useDelayedValue } from "hooks/use_delayed_value";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { CapabilityType } from "shared/types";
import {
  selectActiveBreakoutSessionId,
  selectActiveRoomIdForCurrentUser,
  selectCaptionerIsPresent,
  selectLatestCaption,
  selectShowLiveCaptions,
} from "store/selectors/classroom";

import "./caption.scss";

type LineType = {
  text: string;
  isAppendable: boolean;
};

const MAX_LINES = 3;
const MAX_CHARS_PER_LINE = 65;
const NEW_LINE = { text: "", isAppendable: true };
const RESET_DELAY = 9000; // ensure most recent caption shows for minimum duration

function _formatParagraphs(text: string) {
  // new lines are compressed so that multiple new lines do not delay displaying text
  const compressed = text.replaceAll(/\n+/g, "\n");
  return compressed.split("\n");
}

function _getDelimiter(index: number, paragraph: string) {
  // prevents splitting words across lines
  const textContinues = !!paragraph.slice(index).match(/\S+/g);
  if (textContinues) {
    for (let x = index; x >= 0; x -= 1) {
      const char = paragraph[x];
      if (char !== undefined && char.match(/\s/)) {
        return x + 1;
      }
    }
  }
  return index;
}

function CaptionsOutput() {
  const activeBreakoutSessionId = useAppSelector(selectActiveBreakoutSessionId());
  const currentUserRoomId = useAppSelector(selectActiveRoomIdForCurrentUser());
  const userHasLiveCaptionsEnabled = useAppSelector(selectShowLiveCaptions());
  const captionerIsPresent = useAppSelector(selectCaptionerIsPresent());
  const breakoutNotificationWasShown = useRef(false);
  const canParticipateInClass = useCapability(CapabilityType.CAN_PARTICIPATE_IN_CLASS);
  const toastIdRef = useRef<Id | null>(null);
  const widthAdjustment = useSidebarAdjustment();

  // Only the latest 3 (MAX_LINES) captions are displayed at a time,
  // - currentLines is an array that keeps track of the captions being displayed
  // - the rest are queued up in the queue array.
  // We use a ref for both these variables as we need them to be mutable.
  const [currentLines, setCurrentLines] = useState<LineType[]>([NEW_LINE, NEW_LINE, NEW_LINE]);
  const queue = useRef<string[]>([]);
  // removeLineCount is used to keep track of when a line should be removed
  const removeLineCount = useRef(0);

  const latestCaption = useAppSelector(selectLatestCaption());

  useEffect(() => {
    function _enqueue(newParagraphs: string[]) {
      if (!newParagraphs.length) return;
      const prevParagraph = queue.current.pop() || "";
      const firstNewParagraph = newParagraphs.shift() as string;
      const updatedPrevParagraph = prevParagraph.concat(firstNewParagraph);
      queue.current = [...queue.current, updatedPrevParagraph, ...newParagraphs];
    }

    function _removeFirstLine() {
      const newLines = [...currentLines];
      newLines.shift();
      newLines.push({ ...NEW_LINE });
      return newLines;
    }

    // Adds text to a line if it is appendable and mark it as not appendable when it is full
    function _updateLine(lineData: LineType) {
      const emptySpaces = MAX_CHARS_PER_LINE - lineData.text.length;
      const index = emptySpaces + 1;
      const paragraph = queue.current.shift() || "";
      const delimiter = _getDelimiter(index, paragraph);
      const newText = paragraph.slice(0, delimiter);
      const newLine = lineData.text.concat(newText);
      const remainingText = paragraph.slice(delimiter);
      const newLineData: LineType = { ...lineData };
      newLineData.text = newLine;
      if (remainingText.length || queue.current.length) {
        newLineData.isAppendable = false;
      }

      if (remainingText.length) {
        queue.current.unshift(remainingText);
      }
      return newLineData;
    }

    // Updates the captions by adding new lines, adding text to existing lines and removing old lines
    function _updateCaptions() {
      // remove one line at a time when there is nothing to add;
      // doing so prevents text changes that are hard to track visually
      if (!queue.current[0] && queue.current.length === 1) {
        removeLineCount.current += 1;
        // this delay allows each line to be displayed at least the amount of time
        // it would have been displayed had the captions continued
        if (removeLineCount.current > MAX_LINES) {
          const newLines = _removeFirstLine();
          if (
            newLines.some(
              ({ text, isAppendable }, index) =>
                text !== currentLines[index].text ||
                isAppendable !== currentLines[index].isAppendable,
            )
          ) {
            setCurrentLines(newLines);
          }
        }
        return;
      }

      removeLineCount.current = 0;

      // make room if necessary
      const newLineRequired = currentLines.every((lineData) => !lineData.isAppendable);
      const newLines = newLineRequired ? _removeFirstLine() : [...currentLines];
      let lineChanged = newLineRequired;
      let newLine: LineType;
      for (let x = 0; x < MAX_LINES; x += 1) {
        const lineData = newLines[x];
        if (lineData.isAppendable) {
          newLine = _updateLine(lineData);
          if (newLine.text !== lineData.text || newLine.isAppendable !== lineData.isAppendable) {
            lineChanged = true;
          }
          newLines[x] = newLine;
        }
      }
      if (lineChanged) {
        setCurrentLines(newLines);
      }
    }

    let newParagraphs: string[] = [];

    if (latestCaption.room_id !== currentUserRoomId) {
      if (
        activeBreakoutSessionId &&
        canParticipateInClass &&
        userHasLiveCaptionsEnabled &&
        captionerIsPresent &&
        !breakoutNotificationWasShown.current
      ) {
        toastIdRef.current = createCaptionerIsInAnotherRoomToast();
        breakoutNotificationWasShown.current = true;
      }
      // If the captioner is not in the same room as the user, we do not display the captions.
      // Instead, we finish displaying the rest of the queue.
      if (currentLines.every((line) => !line.text)) {
        return;
      }
      window.flightLogger.log(
        "[CaptionsOutput] Captioner is not in the same room as the user: let's finish displaying previous captions",
      );
      newParagraphs = _formatParagraphs("");
    } else {
      // resets
      breakoutNotificationWasShown.current = false;
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }

      newParagraphs = _formatParagraphs(latestCaption.text);
    }

    _enqueue(newParagraphs);
    _updateCaptions();

    // We need to update captions if there is a new caption, that is if the caption created_on date has changed,
    // independently of the text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestCaption.created_on]);

  const clearCaptionsTimeout = useRef<NodeJS.Timeout>();

  // If the captioner leaves, we clear the captions after a while otherwise it will be displayed indefinitely.
  useEffect(() => {
    if (!captionerIsPresent) {
      clearCaptionsTimeout.current = setTimeout(() => {
        removeLineCount.current = 0;
        queue.current = [];
        setCurrentLines([NEW_LINE, NEW_LINE, NEW_LINE]);
      }, RESET_DELAY);
    } else {
      clearTimeout(clearCaptionsTimeout.current);
    }

    return () => clearTimeout(clearCaptionsTimeout.current);
  }, [captionerIsPresent]);

  // For a smooth transition, we need to keep the captions displayed during the transition.
  // When the captions are displayed, we immediately make them visible before the transition starts.
  // When the captions are hidden, we delay the hiding of the captions until the transition ends.
  const liveCaptionsLaterDisplayed = useDelayedValue(userHasLiveCaptionsEnabled, 0, 1000);

  return (
    <div
      style={{
        height: liveCaptionsLaterDisplayed ? "120px" : "0px",
        transform: userHasLiveCaptionsEnabled ? `translateY(0%)` : "translateY(100%)",
        transition: "transform 1s ease",
        width: widthAdjustment,
      }}
    >
      {liveCaptionsLaterDisplayed && (
        <Flex
          className="items-center bg-black text-black-tint-95 py6"
          direction="column"
          data-testid="captions-output"
        >
          {currentLines.map((line, index) => {
            const key = (Math.random() + 1).toString(36).substring(2);
            return (
              <div key={key} className="body-l" data-testid={`captions-output-line-${index}`}>
                {line.text}
              </div>
            );
          })}
        </Flex>
      )}
    </div>
  );
}

export { CaptionsOutput };
